import { z } from "zod";
import { isIsoCountryCode } from "@libs/utils/isoCountries";

/**
 * Lissen's regions, where a Merch section's fans can be told apart: the UK
 * (GB), the EU (its member states), North America (the US and Canada) and
 * International (every other country; its key stays `rest_of_world`). A
 * fan's region is their
 * country's ({@link merchTerritoryOfCountry}); a product sells in each
 * region it is on sale to. A Merch section's own picks are for All markets;
 * a region can have its own list instead, picked in the Link Editor's and
 * the Site Editor's Merch card ("Picks for").
 *
 * `north_america` was the only territory before the regions, so a section
 * saved then keeps its North America picks with no migration.
 */
export const MERCH_TERRITORIES = ["uk", "eu", "north_america", "rest_of_world"] as const;
export type MerchTerritory = (typeof MERCH_TERRITORIES)[number];
export const merchTerritorySchema = z.enum(MERCH_TERRITORIES);

/** The territories a page served before the regions (`data.territories`):
 *  an older page or site bundle parses only these keys, so the new regions
 *  go in `data.regions`. */
export const LEGACY_MERCH_TERRITORIES = ["north_america"] as const;
export const legacyMerchTerritorySchema = z.enum(LEGACY_MERCH_TERRITORIES);

/** The stores a merch product sells from, by region: each store one of
 *  its listings sells to a country of the region from (its host, or the
 *  seller's name). Auto shows a product of each store a region's products
 *  sell from. */
export const merchStoresByRegionSchema = z.partialRecord(merchTerritorySchema, z.array(z.string().min(1)));
export type MerchStoresByRegion = z.infer<typeof merchStoresByRegionSchema>;

/** How a region is named to the artist. */
export const MERCH_TERRITORY_LABELS: Record<MerchTerritory, string> = {
  uk: "UK",
  eu: "EU",
  north_america: "North America",
  rest_of_world: "International",
};
/** How a Merch section's own picks, for every region without its own
 *  list, are named to the artist. */
export const MERCH_HOME_TERRITORY_LABEL = "All markets";

/** The EU's member states (ISO 3166-1 alpha-2). */
export const EU_MEMBER_STATES: ReadonlySet<string> = new Set([
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU", "IE",
  "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES", "SE",
]);

/** The region of a fan or a listing country in `country` (ISO 3166-1
 *  alpha-2); null for an unknown country. */
export function merchTerritoryOfCountry(
  country: string | null | undefined,
): MerchTerritory | null {
  const code = country?.trim().toUpperCase();
  if (!code || !isIsoCountryCode(code)) return null;
  if (code === "GB") return "uk";
  if (EU_MEMBER_STATES.has(code)) return "eu";
  if (code === "US" || code === "CA") return "north_america";
  return "rest_of_world";
}

/** The regions of the countries a product is on sale to, in
 *  {@link MERCH_TERRITORIES}' order, once each. */
export function merchTerritoriesOfCountries(
  countries: Iterable<string>,
): MerchTerritory[] {
  const found = new Set<MerchTerritory>();
  for (const country of countries) {
    const territory = merchTerritoryOfCountry(country);
    if (territory) found.add(territory);
  }
  return MERCH_TERRITORIES.filter((territory) => found.has(territory));
}

/**
 * Where a product sells when nothing says which countries it is on sale
 * to (a product served by an api older than its `regions`): the region of
 * the currency its store sells it in to the UK. Dollars (US or Canadian)
 * are North America, euros the EU, pounds the UK, and any other
 * International.
 */
export function merchTerritoriesOfCurrency(
  currency: string | null | undefined,
): MerchTerritory[] {
  switch (currency) {
    case "USD":
    case "CAD":
      return ["north_america"];
    case "EUR":
      return ["eu"];
    case "GBP":
    case null:
    case undefined:
      return ["uk"];
    default:
      return ["rest_of_world"];
  }
}
